import React, { useState } from 'react';
import { Box, Paper, TextField, Button, Typography, CircularProgress, Snackbar, Stack, Divider, InputAdornment } from '@mui/material';
import Img from '../../../images/Phyfarm-App-footer.png';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import AuthLayout from '../../../shared/layouts/AuthLayout';
import { useSelector } from 'react-redux';
import { requestPasswordReset } from '../services/authService';

const ForgotPasswordPage = () => {

    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const baseURL = useSelector((state) => state.environmentReducer.environment);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const isSmScreen = useMediaQuery('(max-width:850px)');


    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };


    const handleEmailChange = (event) => {
        setEmail(event.target.value);
    };

    const handleSendEmail = async () => {

        if (email === '') {
            setError('Empty fields');
            setSnackbarOpen(true);
            return;
        }
        try {

            setLoading(true);
            const response = await requestPasswordReset(baseURL, email);
            const data = await response.json();
            if (data.data) {
                navigate("/confirmation", { replace: true });
                localStorage.setItem('resetMail', JSON.stringify(email));
            }
            else if (data?.error?.status === 400) {
                setError(
                    "This email does not exist");
                setSnackbarOpen(true);
            } else {
                setError('An error occurred while trying to sending link.');
                setSnackbarOpen(true);
            }
        } catch (error) {

            setError('An error occurred while sending link.');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout fullBleed>
            <Box
                sx={{
                    minHeight: '100vh',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    py: 6,
                    backgroundImage: `url(${Img})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                            'linear-gradient(135deg, rgba(8,28,18,0.92) 0%, rgba(10,46,31,0.82) 45%, rgba(245,244,238,0.70) 100%)',
                    }}
                />

                <Paper
                    elevation={0}
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        width: '100%',
                        maxWidth: 980,
                        borderRadius: 4,
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.14)',
                        boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
                        backdropFilter: 'blur(14px)',
                        background: 'rgba(255,255,255,0.10)',
                    }}
                >
                    <Box sx={{ display: 'grid', gridTemplateColumns: isSmScreen ? '1fr' : '1.15fr 0.85fr' }}>
                        {/* Brand / Info */}
                        <Box
                            sx={{
                                p: isSmScreen ? 3 : 4,
                                color: 'white',
                                background:
                                    'linear-gradient(160deg, rgba(8,28,18,0.70) 0%, rgba(10,46,31,0.40) 55%, rgba(10,46,31,0.15) 100%)',
                                borderRight: isSmScreen ? 'none' : '1px solid rgba(255,255,255,0.10)',
                            }}
                        >
                            <Typography sx={{ fontFamily: '"DM Mono", monospace', fontSize: 11, letterSpacing: '0.18em', opacity: 0.7 }}>
                                PHYFARM
                            </Typography>
                            <Typography sx={{ mt: 0.75, fontFamily: '"Playfair Display", serif', fontWeight: 900, fontSize: 34, lineHeight: 1.05 }}>
                                Farm Intelligence Platform
                            </Typography>
                            <Typography sx={{ mt: 1.25, color: 'rgba(167,243,208,0.85)', fontFamily: '"DM Sans", sans-serif', maxWidth: 440 }}>
                                Reset your password and get back to managing farms, sensors, and insights.
                            </Typography>
                        </Box>

                        {/* Form */}
                        <Box
                            sx={{
                                p: isSmScreen ? 3 : 4,
                                background: 'rgba(250,250,245,0.92)',
                            }}
                        >
                            <Typography sx={{ fontFamily: '"DM Mono", monospace', fontSize: 11, letterSpacing: '0.16em', color: '#1A7A50' }}>
                                ACCOUNT RECOVERY
                            </Typography>
                            <Typography sx={{ mt: 0.6, fontFamily: '"Playfair Display", serif', fontWeight: 800, fontSize: 26, color: '#0A2E1F' }}>
                                Forgot password
                            </Typography>
                            <Typography sx={{ mt: 0.75, color: '#6B8A7A', fontSize: 13 }}>
                                Enter your registered email address. We’ll send a reset link.
                            </Typography>

                            <Divider sx={{ my: 2.5, borderColor: 'rgba(10,46,31,0.10)' }} />

                            <Stack spacing={1.75}>
                                <TextField
                                    label="Email"
                                    value={email}
                                    onChange={handleEmailChange}
                                    placeholder="name@company.com"
                                    error={email === '' && error !== ''}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ color: '#6B8A7A' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleSendEmail}
                                    sx={{ py: 1.2 }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Send reset link'}
                                </Button>

                                <Button
                                    variant="text"
                                    color="success"
                                    onClick={() => navigate('/', { replace: true })}
                                    sx={{ fontWeight: 800 }}
                                >
                                    Back to sign in
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                </Paper>
            </Box>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Alert onClose={handleSnackbarClose} severity="error">
                    {error}
                </Alert>
            </Snackbar>
        </AuthLayout>

    );
};

export default ForgotPasswordPage;
